import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, DollarSign, ShoppingCart, RotateCcw, Package, Calendar, Download, Award, BarChart3 } from 'lucide-react';
import Authenticated from '../../layout/Authenticated';
import axiosInstance from '../../api/axiosInstance';
import * as XLSX from 'xlsx';

const MonthlyReport = () => {
    const currentDate = new Date();
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i);

    const fetchReport = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get('/reports/monthly', {
                params: {
                    month: selectedMonth,
                    year: selectedYear
                }
            });
            setReportData(response.data.data);
        } catch (err) {
            console.error('Error fetching report:', err);
            setError('Failed to load report. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [selectedMonth, selectedYear]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const downloadReport = () => {
        if (!reportData) return;

        // Create workbook
        const workbook = XLSX.utils.book_new();

        // ========== SUMMARY SHEET ==========
        const summaryData = [
            ['MONTHLY PHARMACY REPORT'],
            [`Period: ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`],
            [''],
            ['SALES SUMMARY'],
            ['Total Invoices', reportData.summary.sales.total_invoices],
            ['Total Revenue', `₹${reportData.summary.sales.total_revenue}`],
            ['Average Invoice Value', `₹${reportData.summary.sales.average_invoice_value}`],
            [''],
            ['PROFITABILITY'],
            ['Gross Revenue', `₹${reportData.summary.profitability.gross_revenue}`],
            ['Net Revenue', `₹${reportData.summary.profitability.net_revenue}`],
            ['Cost of Goods Sold', `₹${reportData.summary.profitability.cost_of_goods_sold}`],
            ['Gross Profit', `₹${reportData.summary.profitability.gross_profit}`],
            ['Net Profit', `₹${reportData.summary.profitability.net_profit}`],
            ['Profit Margin', `${reportData.summary.profitability.profit_margin}%`],
            [''],
            ['PURCHASES'],
            ['Total Purchases', reportData.summary.purchases.total_purchases],
            ['Total Amount', `₹${reportData.summary.purchases.total_amount}`],
            [''],
            ['RETURNS'],
            ['Sales Returns Count', reportData.summary.sales_returns.total_returns],
            ['Sales Returns Amount', `₹${reportData.summary.sales_returns.total_amount}`],
            ['Purchase Returns Count', reportData.summary.purchase_returns.total_returns],
            ['Purchase Returns Amount', `₹${reportData.summary.purchase_returns.total_amount}`],
        ];
        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

        // ========== BEST SELLING MEDICINES SHEET ==========
        const bestSellingData = [
            ['Rank', 'Medicine Name', 'Manufacturer', 'Category', 'Quantity Sold', 'Total Revenue'],
            ...reportData.best_selling_medicines.map((m, i) => [
                i + 1,
                m.name,
                m.manufacturer,
                m.category,
                m.total_quantity_sold,
                parseFloat(m.total_revenue)
            ])
        ];
        const bestSellingSheet = XLSX.utils.aoa_to_sheet(bestSellingData);
        XLSX.utils.book_append_sheet(workbook, bestSellingSheet, 'Best Selling');

        // ========== MOST PROFITABLE MEDICINES SHEET ==========
        const profitableData = [
            ['Rank', 'Medicine Name', 'Manufacturer', 'Qty Sold', 'Revenue', 'Cost', 'Profit', 'Margin %'],
            ...reportData.most_profitable_medicines.map((m, i) => [
                i + 1,
                m.name,
                m.manufacturer,
                m.total_quantity_sold,
                parseFloat(m.total_revenue),
                parseFloat(m.total_cost),
                parseFloat(m.profit),
                parseFloat(m.profit_margin)
            ])
        ];
        const profitableSheet = XLSX.utils.aoa_to_sheet(profitableData);
        XLSX.utils.book_append_sheet(workbook, profitableSheet, 'Most Profitable');

        // ========== DAILY BREAKDOWN SHEET ==========
        if (reportData.daily_breakdown && reportData.daily_breakdown.length > 0) {
            const dailyData = [
                ['Date', 'Invoice Count', 'Daily Revenue'],
                ...reportData.daily_breakdown.map(day => [
                    new Date(day.date).toLocaleDateString('en-IN'),
                    day.invoice_count,
                    parseFloat(day.revenue)
                ])
            ];
            const dailySheet = XLSX.utils.aoa_to_sheet(dailyData);
            XLSX.utils.book_append_sheet(workbook, dailySheet, 'Daily Breakdown');
        }

        // Download the Excel file
        XLSX.writeFile(workbook, `monthly_report_${months.find(m => m.value === selectedMonth)?.label}_${selectedYear}.xlsx`);
    };

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-4 px-3 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6 border border-teal-100">
                        <div className="bg-gradient-to-r from-teal-600 to-teal-500 p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex items-center space-x-3">
                                    <div className="bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-xl">
                                        <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                                    </div>
                                    <div>
                                        <h1 className="text-xl sm:text-2xl font-bold text-white">Monthly Report</h1>
                                        <p className="text-teal-100 text-xs sm:text-sm mt-1">Comprehensive business analytics</p>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <select
                                        value={selectedMonth}
                                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                        className="px-4 py-2 bg-white/10 border border-white/30 rounded-lg text-white backdrop-blur-sm outline-none focus:ring-2 focus:ring-white/50"
                                    >
                                        {months.map(month => (
                                            <option key={month.value} value={month.value} className="text-gray-800">
                                                {month.label}
                                            </option>
                                        ))}
                                    </select>

                                    <select
                                        value={selectedYear}
                                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                        className="px-4 py-2 bg-white/10 border border-white/30 rounded-lg text-white backdrop-blur-sm outline-none focus:ring-2 focus:ring-white/50"
                                    >
                                        {years.map(year => (
                                            <option key={year} value={year} className="text-gray-800">
                                                {year}
                                            </option>
                                        ))}
                                    </select>

                                    {reportData && (
                                        <button
                                            onClick={downloadReport}
                                            className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/30 rounded-lg text-white backdrop-blur-sm hover:bg-white/20 transition-all"
                                        >
                                            <Download className="w-4 h-4" />
                                            <span className="hidden sm:inline">Download</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg mb-6">
                            <p className="text-red-700">{error}</p>
                        </div>
                    )}

                    {reportData && !loading && (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                {/* Sales Card */}
                                <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-teal-500">
                                    <div className="flex items-center justify-between mb-2">
                                        <ShoppingCart className="w-8 h-8 text-teal-600" />
                                        <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2 py-1 rounded">
                                            {reportData.summary.sales.total_invoices} Invoices
                                        </span>
                                    </div>
                                    <h3 className="text-gray-600 text-sm font-medium">Total Sales</h3>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">
                                        ₹{parseFloat(reportData.summary.sales.total_revenue).toLocaleString('en-IN')}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Avg: ₹{parseFloat(reportData.summary.sales.average_invoice_value).toLocaleString('en-IN')}
                                    </p>
                                </div>

                                {/* Profit Card */}
                                <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-green-500">
                                    <div className="flex items-center justify-between mb-2">
                                        <TrendingUp className="w-8 h-8 text-green-600" />
                                        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">
                                            {reportData.summary.profitability.profit_margin}% Margin
                                        </span>
                                    </div>
                                    <h3 className="text-gray-600 text-sm font-medium">Net Profit</h3>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">
                                        ₹{parseFloat(reportData.summary.profitability.net_profit).toLocaleString('en-IN')}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Gross: ₹{parseFloat(reportData.summary.profitability.gross_profit).toLocaleString('en-IN')}
                                    </p>
                                </div>

                                {/* Purchases Card */}
                                <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-blue-500">
                                    <div className="flex items-center justify-between mb-2">
                                        <Package className="w-8 h-8 text-blue-600" />
                                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                            {reportData.summary.purchases.total_purchases} Orders
                                        </span>
                                    </div>
                                    <h3 className="text-gray-600 text-sm font-medium">Total Purchases</h3>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">
                                        ₹{parseFloat(reportData.summary.purchases.total_amount).toLocaleString('en-IN')}
                                    </p>
                                </div>

                                {/* Returns Card */}
                                <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-orange-500">
                                    <div className="flex items-center justify-between mb-2">
                                        <RotateCcw className="w-8 h-8 text-orange-600" />
                                        <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded">
                                            {reportData.summary.sales_returns.total_returns} Returns
                                        </span>
                                    </div>
                                    <h3 className="text-gray-600 text-sm font-medium">Sales Returns</h3>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">
                                        ₹{parseFloat(reportData.summary.sales_returns.total_amount).toLocaleString('en-IN')}
                                    </p>
                                </div>
                            </div>

                            {/* Best Selling Medicines */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Award className="w-6 h-6 text-teal-600" />
                                        <h2 className="text-lg font-bold text-gray-800">Best Selling Medicines</h2>
                                    </div>
                                    <div className="space-y-3">
                                        {reportData.best_selling_medicines.slice(0, 5).map((medicine, index) => (
                                            <div key={medicine.medicine_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-teal-50 transition-colors">
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    <div className="flex-shrink-0 w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                                                        <span className="text-teal-700 font-bold text-sm">{index + 1}</span>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="font-semibold text-gray-900 text-sm truncate">{medicine.name}</p>
                                                        <p className="text-xs text-gray-500">{medicine.manufacturer}</p>
                                                    </div>
                                                </div>
                                                <div className="text-right ml-2">
                                                    <p className="font-bold text-teal-700">{medicine.total_quantity_sold}</p>
                                                    <p className="text-xs text-gray-500">₹{parseFloat(medicine.total_revenue).toLocaleString('en-IN')}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Most Profitable Medicines */}
                                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                                    <div className="flex items-center gap-3 mb-4">
                                        <DollarSign className="w-6 h-6 text-green-600" />
                                        <h2 className="text-lg font-bold text-gray-800">Most Profitable Medicines</h2>
                                    </div>
                                    <div className="space-y-3">
                                        {reportData.most_profitable_medicines.slice(0, 5).map((medicine, index) => (
                                            <div key={medicine.medicine_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                                        <span className="text-green-700 font-bold text-sm">{index + 1}</span>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="font-semibold text-gray-900 text-sm truncate">{medicine.name}</p>
                                                        <p className="text-xs text-gray-500">{medicine.profit_margin}% margin</p>
                                                    </div>
                                                </div>
                                                <div className="text-right ml-2">
                                                    <p className="font-bold text-green-700">₹{parseFloat(medicine.profit).toLocaleString('en-IN')}</p>
                                                    <p className="text-xs text-gray-500">{medicine.total_quantity_sold} sold</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Daily Breakdown */}
                            {reportData.daily_breakdown && reportData.daily_breakdown.length > 0 && (
                                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Calendar className="w-6 h-6 text-blue-600" />
                                        <h2 className="text-lg font-bold text-gray-800">Daily Revenue Breakdown</h2>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Invoices</th>
                                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Revenue</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {reportData.daily_breakdown.map((day, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            {new Date(day.date).toLocaleDateString('en-IN', {
                                                                day: '2-digit',
                                                                month: 'short'
                                                            })}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                            {day.invoice_count}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                                                            ₹{parseFloat(day.revenue).toLocaleString('en-IN')}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Authenticated>
    );
};

export default MonthlyReport;
